import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import authService from '../../services/authService';
import { hasFeatureAccess, filterNavigationByRole } from '../../utils/roleBasedAccess';

const Header = ({ currentUser, notificationCounts = {}, onNavigate }) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Get user role for navigation filtering
  useEffect(() => {
    const getUserRole = async () => {
      try {
        const user = currentUser || authService.getStoredUser() || await authService.getCurrentUser();
        console.log('[Header] Setting user role:', user?.role);
        setUserRole(user?.role);
      } catch (error) {
        console.warn('[Header] Could not get user role:', error);
        setUserRole(null);
      }
    };

    getUserRole();
  }, [currentUser]);

  // Navigation items with role-based filtering
  const allNavigationItems = [
    {
      label: 'Dashboard',
      path: userRole === 'hr_coordinator' ? '/hr-roles/coordinator' : '/hr-dashboard',
      icon: 'LayoutDashboard',
      tooltip: 'Dashboard Overview',
      featureKey: 'dashboard'
    },
    {
      label: 'Candidates',
      path: '/candidate-database',
      icon: 'Users',
      badge: notificationCounts?.candidates || 0,
      tooltip: 'Candidate Database',
      featureKey: 'candidates'
    },
    {
      label: 'Processing',
      path: '/cv-upload-processing',
      icon: 'Upload',
      badge: notificationCounts?.processing || 0,
      tooltip: 'CV Upload & Processing',
      featureKey: 'cv-processing'
    },
    {
      label: 'Interviews',
      path: '/interview-scheduling',
      icon: 'Calendar',
      badge: notificationCounts?.interviews || 0,
      tooltip: 'Interview Scheduling',
      featureKey: 'interviews'
    }
  ];

  // Filter navigation items based on user role, but show basic items if role not loaded
  const navigationItems = userRole 
    ? filterNavigationByRole(allNavigationItems, userRole)
    : allNavigationItems.filter(item => ['candidates', 'interviews'].includes(item.featureKey)); // Default for coordinator

  // Debug log to track role changes
  useEffect(() => {
    console.log('[Header] User role changed:', userRole);
    console.log('[Header] Available navigation items:', navigationItems?.length);
  }, [userRole, navigationItems]);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    if (onNavigate) {
      onNavigate(path);
    }
  };

  const handleLogout = () => {
    console.log('[Header] Logout initiated');
    setIsUserMenuOpen(false);
    
    // Clear authentication data and redirect
    authService.logout();
  };

  const isActivePath = (path) => {
    if (path === '/hr-dashboard') {
      return location?.pathname === path;
    }
    return location?.pathname?.startsWith(path);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserMenuOpen && !event?.target?.closest('.user-menu-container')) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 z-[100] bg-surface border-b border-border">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6">
        {/* Logo Section */}
        <div className="flex items-center">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Brain" size={20} color="white" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg font-semibold text-foreground">C-success recruit</h1>
              <p className="text-xs text-muted-foreground -mt-1">Intelligent Recruitment</p>
            </div>
            {/* Compact logo for mobile */}
            <div className="block sm:hidden">
              <h1 className="text-base font-semibold text-foreground">CSR</h1>
            </div>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1">
          {navigationItems?.map((item) => (
            <button
              key={item?.path}
              onClick={() => handleNavigation(item?.path)}
              className={`
                relative flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium
                transition-all duration-200 micro-feedback
                ${isActivePath(item?.path)
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }
              `}
              title={item?.tooltip}
            >
              <Icon name={item?.icon} size={16} />
              <span className="hidden lg:inline">{item?.label}</span>
              {item?.badge > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-accent-foreground text-xs rounded-full flex items-center justify-center font-medium">
                  {item?.badge > 99 ? '99+' : item?.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Right Section */}
        <div className="flex items-center space-x-2 sm:space-x-4">
          {/* Notifications */}
          <button className="relative p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors micro-feedback">
            <Icon name="Bell" size={20} />
            {(notificationCounts?.total || 0) > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-accent-foreground text-xs rounded-full flex items-center justify-center font-medium">
                {notificationCounts?.total > 99 ? '99+' : notificationCounts?.total}
              </span>
            )}
          </button>

          {/* User Menu */}
          <div className="relative user-menu-container">
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted transition-colors micro-feedback"
            >
              <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                <Icon name="User" size={16} color="white" />
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-foreground truncate max-w-[120px]">
                  {currentUser?.name || 'HR Manager'}
                </p>
                <p className="text-xs text-muted-foreground truncate max-w-[120px]">
                  {currentUser?.role || 'Senior Recruiter'}
                </p>
              </div>
              <Icon 
                name="ChevronDown" 
                size={16} 
                className={`text-muted-foreground transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {/* User Dropdown */}
            {isUserMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-popover border border-border rounded-lg shadow-elevation-modal z-50">
                <div className="p-3 border-b border-border">
                  <p className="font-medium text-popover-foreground truncate">
                    {currentUser?.name || 'HR Manager'}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    {currentUser?.email || 'hr@company.com'}
                  </p>
                </div>
                <div className="py-2">
                  <button className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors">
                    <Icon name="User" size={16} />
                    <span>Profile Settings</span>
                  </button>
                  <button className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors">
                    <Icon name="Settings" size={16} />
                    <span>Preferences</span>
                  </button>
                  <button className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors">
                    <Icon name="HelpCircle" size={16} />
                    <span>Help & Support</span>
                  </button>
                  <div className="border-t border-border mt-2 pt-2">
                    <button 
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                    >
                      <Icon name="LogOut" size={16} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors micro-feedback"
          >
            <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
          </button>
        </div>
      </div>
      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-surface border-t border-border">
          <nav className="px-4 py-4 space-y-2">
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`
                  relative w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium
                  transition-all duration-200 micro-feedback
                  ${isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }
                `}
              >
                <Icon name={item?.icon} size={18} />
                <span>{item?.label}</span>
                {item?.badge > 0 && (
                  <span className="ml-auto w-6 h-6 bg-accent text-accent-foreground text-xs rounded-full flex items-center justify-center font-medium">
                    {item?.badge > 99 ? '99+' : item?.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;